
import { useState } from 'react'
import { Plus, Search, Filter, Edit, Trash2, MoreHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { CustomFoodForm } from '@/components/diet-plans-v2/custom-food-form'
import { useCustomFoods, useDeleteCustomFood, useSearchFoods } from '@/api/food-library'
import { useDebounce } from 'use-debounce'
import type { FoodItem } from '@/types/diet-v2'
import { PageHeader } from '@/components/shared/page-header'
import { TableEmptyRow, TableLoadingRows } from '@/components/shared/table-states'

export default function FoodLibraryPage() {
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingFood, setEditingFood] = useState<FoodItem | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedQuery] = useDebounce(searchQuery, 500)

  const { data: customFoods = [], isLoading: isLoadingCustom } = useCustomFoods()
  const { data: searchResults = [], isLoading: isSearching } = useSearchFoods(debouncedQuery, undefined)
  const deleteMutation = useDeleteCustomFood()

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this custom food?')) {
      await deleteMutation.mutateAsync(id)
    }
  }

  const isLoadingFoods = isLoadingCustom || isSearching
  const displayFoods = debouncedQuery ? searchResults : customFoods

  return (
    <div className="space-y-6">
      <PageHeader
        title="Food Library"
        description="Manage custom foods and search USDA database"
      >
        <Button onClick={() => setShowAddDialog(true)} className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Add Custom Food
        </Button>
      </PageHeader>

      {/* Search Bar */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search for foods (USDA + Custom)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>
        </CardContent>
      </Card>

      {/* Unified Food List */}
      <Card>
        <CardHeader>
          <CardTitle>
            {debouncedQuery ? `Search Results for "${debouncedQuery}"` : 'Food Library'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Serving Size</TableHead>
                <TableHead>Calories</TableHead>
                <TableHead>Protein</TableHead>
                <TableHead>Carbs</TableHead>
                <TableHead>Fat</TableHead>
                <TableHead>Fiber</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingFoods ? (
                <TableLoadingRows colSpan={9} />
              ) : displayFoods.length === 0 ? (
                <TableEmptyRow
                  colSpan={9}
                  message={
                    debouncedQuery
                      ? `No foods found matching "${debouncedQuery}"`
                      : 'No custom foods yet. Create your first one!'
                  }
                  action={
                    !debouncedQuery ? (
                      <Button className="mt-2" onClick={() => setShowAddDialog(true)}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Custom Food
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                displayFoods.map((food) => (
                  <TableRow key={food._id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{food.name}</span>
                        {food.fdcId && (
                          <span className="text-xs text-muted-foreground">ID: {food.fdcId}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={food.source === 'usda' ? 'default' : 'secondary'}>
                        {food.source === 'usda' ? 'USDA' : 'Custom'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {food.servingSize} {food.servingUnit}
                    </TableCell>
                    <TableCell>{food.calories} kcal</TableCell>
                    <TableCell>{food.macros.protein}g</TableCell>
                    <TableCell>{food.macros.carbs}g</TableCell>
                    <TableCell>{food.macros.fat}g</TableCell>
                    <TableCell>{food.macros.fiber || 0}g</TableCell>
                    <TableCell className="text-right">
                      {food.source === 'custom' && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => {
                              setEditingFood(food)
                              setShowAddDialog(true)
                            }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(food._id)} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Custom Food Dialog */}
      <CustomFoodForm
        open={showAddDialog}
        onOpenChange={(open) => {
          setShowAddDialog(open)
          if (!open) setEditingFood(null)
        }}
        food={editingFood || undefined}
        onSuccess={() => {
          setShowAddDialog(false)
          setEditingFood(null)
        }}
      />
    </div>
  )
}
